import { useCallback } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { FileSelectionState, Options } from '@asset-metrix/portal-ui';
import { Editor, JsonError, JsonSchema } from '@sagold/react-json-editor';

import { computeFileInitialData } from '../computeFileInitialData';
import { METADATA_EXTRACTION_TYPE } from '../constants';

export type UsePreValidateFilesParams = {
  tableModalValues?: { [p: string]: Options };
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
  editorInitialData: Record<string, unknown>;
  extractionMethod: METADATA_EXTRACTION_TYPE;
  fileState: FileSelectionState;
  jsonSchema: JsonSchema | undefined;
  setErrors: (fileId: string, errors: JsonError[]) => void;
  perFileMetaData: Record<string, Record<string, unknown>>;
};

/**
 * Hook that provides a function to pre-validate all files before the user visits each page.
 * This allows showing validation status immediately when entering the validate step.
 */
export function usePreValidateFiles({
  tableModalValues,
  fileNameMetadataExtractionConfig,
  editorInitialData,
  extractionMethod,
  fileState,
  jsonSchema,
  setErrors,
  perFileMetaData,
}: UsePreValidateFilesParams) {
  const preValidateAllFiles = useCallback(() => {
    if (!jsonSchema) {
      throw new Error(
        'Pre-validation failed: jsonSchema is undefined. ' +
          'This is a misconfiguration. Ensure the schema has the required ' +
          'conditionalFilter.dataProperty for file name extraction, or ' +
          'perFileInputInUpload options for AI extraction.'
      );
    }

    for (const file of fileState) {
      // Skip files that have already been validated (user visited the page)
      if (file.validationErrors !== undefined) {
        continue;
      }

      // Compute the initial data for this file
      const initialData = computeFileInitialData({
        tableModalValues,
        fileNameMetadataExtractionConfig,
        editorInitialData,
        extractionMethod,
        file,
        fileMetadata: perFileMetaData[file.id],
        jsonSchema,
      });

      // Create a headless editor instance for validation
      const editor = new Editor({
        schema: jsonSchema,
        data: initialData,
        validate: true,
      });

      // Get validation errors
      const errors: JsonError[] = editor.getErrors();

      // Clean up the editor instance
      editor.destroy();

      // Set the errors for this file
      setErrors(file.id, errors);
    }
  }, [
    tableModalValues,
    fileNameMetadataExtractionConfig,
    editorInitialData,
    extractionMethod,
    fileState,
    jsonSchema,
    setErrors,
    perFileMetaData,
  ]);

  return { preValidateAllFiles };
}
